import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Pagination,
  Box,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  getCpSources,
  addCpSource,
  updateCpSource,
  deleteCpSource,
  duplicateCpSource,
} from "../../../Services/Settings";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import { successTost, errorTost } from "../../../Services/Toast";
import cssClasses from "./CpSourceSettings.module.css";

// Rows shown per page in the list.
const PAGE_SIZE = 10;

// Normalize a name to the standard stored format: trimmed, single-spaced, and
// title-cased (first letter of each word capital, rest lowercase).
// e.g. "  gaurav   SAWANT " -> "Gaurav Sawant"
const toTitleCase = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

// Master list of Channel Partner (CP) source names. Each row is one CP name with
// an auto-generated id (cpSourceId) from the cpSourceMapping table. Supports
// add / rename / delete. Names are validated and de-duplicated case-insensitively
// (client-side against the loaded list, then via a DB check, with the unique index
// on lower(sourceName) as the final backstop).
export default function CpSourceSettings() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [page, setPage] = useState(1);
  // Confirmation modal config: { title, subTitle, confirmLabel, onConfirm }
  const [confirmCfg, setConfirmCfg] = useState(null);

  // Client-side pagination over the loaded list (10 per page).
  const pageCount = Math.max(1, Math.ceil(sources.length / PAGE_SIZE));
  const pagedSources = sources.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Keep the current page valid when the list shrinks (e.g. after a delete).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  // (re)fetch the list — called on mount and after every add/edit/delete so the
  // CP names stay enlisted with their generated ids.
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getCpSources();
      setSources(
        (data || []).map((r) => ({ ...r, _editName: r.sourceName }))
      );
    } catch (e) {
      errorTost("Failed to load CP sources.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setRowName = (id, value) => {
    setSources((prev) =>
      prev.map((r) => (r.cpSourceId === id ? { ...r, _editName: value } : r))
    );
  };

  // Name validation shared by add and rename. Returns an error string or null.
  // dupInList catches duplicates against the already-loaded rows immediately
  // (the DB check in the async handlers is the authoritative guard).
  const validateName = (name, excludeId) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return "Source name is required.";
    if (trimmed.length > 100) return "Source name must be 100 characters or fewer.";
    const lower = trimmed.toLowerCase();
    const clash = sources.some(
      (r) => r.cpSourceId !== excludeId && String(r.sourceName).trim().toLowerCase() === lower
    );
    if (clash) return `"${trimmed}" already exists.`;
    return null;
  };

  // --- Add ---
  const addSource = async () => {
    const msg = validateName(newName);
    if (msg) { errorTost(msg); return; }
    const name = toTitleCase(newName);
    setCreating(true);
    try {
      const { count } = await duplicateCpSource(name);
      if (count > 0) { errorTost(`"${name}" already exists.`); return; }
      await addCpSource(name);
      successTost(`Added "${name}".`);
      setNewName("");
      load();
    } catch (e) {
      // 23505 = unique-violation (race against the lower(sourceName) index)
      if (e?.code === "23505") errorTost(`"${name}" already exists.`);
      else errorTost(e?.message || "Failed to add CP source.");
    } finally {
      setCreating(false);
    }
  };

  // --- Save (rename) with confirmation ---
  const requestSave = (row) => {
    const msg = validateName(row._editName, row.cpSourceId);
    if (msg) { errorTost(msg); return; }
    setConfirmCfg({
      title: "Save this CP source?",
      subTitle: `Rename "${row.sourceName}" to "${toTitleCase(row._editName)}".`,
      confirmLabel: "Save",
      onConfirm: () => doSave(row),
    });
  };

  const doSave = async (row) => {
    const name = toTitleCase(row._editName);
    setSavingId(row.cpSourceId);
    try {
      const { count } = await duplicateCpSource(name, row.cpSourceId);
      if (count > 0) { errorTost(`"${name}" already exists.`); return; }
      await updateCpSource(row.cpSourceId, name);
      successTost(`Saved "${name}".`);
      load();
    } catch (e) {
      if (e?.code === "23505") errorTost(`"${name}" already exists.`);
      else errorTost(e?.message || "Failed to save CP source.");
    } finally {
      setSavingId(null);
    }
  };

  // --- Delete with confirmation ---
  const requestDelete = (row) => {
    setConfirmCfg({
      title: "Delete this CP source?",
      subTitle: `This permanently removes "${row.sourceName}".`,
      confirmLabel: "Delete",
      onConfirm: () => doDelete(row),
    });
  };

  const doDelete = async (row) => {
    try {
      await deleteCpSource(row.cpSourceId);
      successTost(`Deleted "${row.sourceName}".`);
      load();
    } catch (e) {
      errorTost(e?.message || "Failed to delete CP source.");
    }
  };

  const handleConfirm = (value) => {
    if (value === "confirmed" && confirmCfg?.onConfirm) confirmCfg.onConfirm();
    setConfirmCfg(null);
  };

  // created_at is a timestamptz set by the DB on insert; show a short date.
  const formatCreatedAt = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  };

  // ----- shared MUI theming (Inter, #EAECF0, #F9FAFB, #1570EF) -----
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontFamily: "Inter-Regular",
      fontSize: "1.4rem",
      borderRadius: "8px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#D0D5DD" },
      "&:hover fieldset": { borderColor: "#98A2B3" },
      "&.Mui-focused fieldset": { borderColor: "#1570EF", borderWidth: "1px" },
    },
    "& .MuiInputBase-input": { padding: "0.9rem 1.1rem" },
  };

  const tableSx = {
    minWidth: 640,
    "& .MuiTableCell-head": {
      backgroundColor: "#F9FAFB",
      color: "#667085",
      fontFamily: "Inter-Medium",
      fontSize: "1.2rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.04rem",
      borderBottom: "1px solid #EAECF0",
      padding: "1.2rem 1.6rem",
      whiteSpace: "nowrap",
    },
    "& .MuiTableCell-body": {
      borderBottom: "1px solid #F2F4F7",
      padding: "1.2rem 1.6rem",
      verticalAlign: "middle",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": { borderBottom: "none" },
    "& .addRow .MuiTableCell-body": { backgroundColor: "#FCFCFD" },
  };

  const saveBtnSx = {
    textTransform: "none",
    fontFamily: "Inter-Medium",
    fontSize: "1.3rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    minWidth: "7.2rem",
    boxShadow: "none",
    backgroundColor: "#1570EF",
    borderRadius: "8px",
    px: "1.6rem",
    "&:hover": { backgroundColor: "#175CD3", boxShadow: "none" },
  };

  const addBtnSx = {
    textTransform: "none",
    fontFamily: "Inter-Medium",
    fontSize: "1.3rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    minWidth: "9rem",
    borderRadius: "8px",
    borderColor: "#1570EF",
    color: "#1570EF",
    "&:hover": { borderColor: "#175CD3", backgroundColor: "#EFF8FF" },
  };

  const deleteBtnSx = {
    border: "1px solid #FDA29B",
    borderRadius: "8px",
    color: "#B42318",
    padding: "0.7rem",
    "&:hover": { backgroundColor: "#FEF3F2", borderColor: "#F97066" },
  };

  return (
    <div className={cssClasses.wrapper}>
      <div className={cssClasses.header}>
        <h3 className={cssClasses.heading}>CP Source</h3>
        <p className={cssClasses.subheading}>
          Manage the list of <b>Channel Partner source</b> names. Each name gets an
          auto-generated id. Names must be unique.
        </p>
      </div>

      {loading ? (
        <div className={cssClasses.loading}><CircularProgress /></div>
      ) : (
        <div className={cssClasses.tableCard}>
          <div className={cssClasses.tableScroll}>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" style={{ width: 90 }}>Sr. No.</TableCell>
                  <TableCell>Source Name</TableCell>
                  <TableCell align="center" style={{ width: 160 }}>Created</TableCell>
                  <TableCell align="center" style={{ width: 200 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Add row — kept at the top so adding a CP is always reachable. */}
                <TableRow className="addRow">
                  <TableCell align="center" style={{ color: "#98A2B3" }}>—</TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="New source name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !creating) addSource(); }}
                      inputProps={{ maxLength: 100 }}
                      sx={{ ...fieldSx, minWidth: 200 }}
                    />
                  </TableCell>
                  <TableCell align="center" style={{ color: "#98A2B3" }}>—</TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addSource} disabled={creating} sx={addBtnSx}>
                      {creating ? "Adding…" : "Add CP"}
                    </Button>
                  </TableCell>
                </TableRow>

                {sources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <div className={cssClasses.empty}>No CP sources yet. Add one above.</div>
                    </TableCell>
                  </TableRow>
                )}

                {pagedSources.map((row, idx) => (
                  <TableRow key={row.cpSourceId}>
                    <TableCell align="center" style={{ fontFamily: "Inter-Medium", fontSize: "1.4rem", color: "#475467" }}>
                      {(page - 1) * PAGE_SIZE + idx + 1}
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={row._editName}
                        onChange={(e) => setRowName(row.cpSourceId, e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        sx={{ ...fieldSx, minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell align="center" style={{ fontFamily: "Inter-Regular", fontSize: "1.3rem", color: "#667085", whiteSpace: "nowrap" }}>
                      {formatCreatedAt(row.created_at)}
                    </TableCell>
                    <TableCell align="center">
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => requestSave(row)}
                          disabled={savingId === row.cpSourceId || row._editName.trim() === row.sourceName}
                          sx={saveBtnSx}
                        >
                          {savingId === row.cpSourceId ? "Saving…" : "Save"}
                        </Button>
                        <Tooltip title="Delete CP source">
                          <IconButton size="small" onClick={() => requestDelete(row)} sx={deleteBtnSx}>
                            <DeleteOutlineIcon style={{ fontSize: "2rem" }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #EAECF0" }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                shape="rounded"
                size="small"
                sx={{ "& .Mui-selected": { background: "#1570EF !important", color: "#fff" } }}
              />
            </Box>
          )}
        </div>
      )}

      {confirmCfg && (
        <ConfirmationAlert
          open={!!confirmCfg}
          close={() => setConfirmCfg(null)}
          title={confirmCfg.title}
          subTitle={confirmCfg.subTitle}
          buttonTitle="Cancel"
          returnRespButton={confirmCfg.confirmLabel}
          confirm={handleConfirm}
          hideCloseIcon
        />
      )}
    </div>
  );
}
